import { Ref, forwardRef, useImperativeHandle } from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { RefetchFnDynamic, graphql, useRefetchableFragment } from "react-relay";
import { generatePath, To, useParams } from "react-router";
import { OperationType } from "relay-runtime";
import { Pencil01Icon } from "@/icons";
import { Link } from "@/kit/ui/link";
import { CompetitionScoringCriteriaFragment$key } from "./__generated__/CompetitionScoringCriteriaFragment.graphql";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";

const CompetitionScoringCriteriaFragment = graphql`
  fragment CompetitionScoringCriteriaFragment on Competition
  @refetchable(queryName: "CompetitionScoringCriteriaFragmentQuery")
  @argumentDefinitions(stage: { type: "Int" }) {
    slug
    stage(stage: $stage) {
      seq
      viewerCanEdit: viewerCan(action: UPDATE_COMPETITION_STAGE)
      scoringCriteria {
        id
        title
        description
        weight
      }
    }
  }
`;

interface Props {
  competition: CompetitionScoringCriteriaFragment$key;
}

export interface CompetitionScoringCriteriaHandle {
  refetch: RefetchFnDynamic<
    OperationType,
    CompetitionScoringCriteriaFragment$key
  >;
}

export default forwardRef(function CompetitionScoringCriteria(
  { competition: competitionFragment }: Props,
  ref: Ref<CompetitionScoringCriteriaHandle>,
) {
  const { slug: eventSlug = "" } = useParams();
  const [{ slug, stage }, refetch] = useRefetchableFragment(
    CompetitionScoringCriteriaFragment,
    competitionFragment,
  );
  useImperativeHandle(ref, () => ({ refetch }), [refetch]);

  if (!stage) return null;
  const { viewerCanEdit, scoringCriteria } = stage;

  const editLocation: To = {
    pathname: generatePath("/challenges/:owner/tracks/:slug/scoring-criteria", {
      owner: eventSlug,
      slug,
    }),
    search: new URLSearchParams({ stage: stage.seq.toString(10) }).toString(),
  };

  if (
    scoringCriteria.every(
      ({ description, weight }) => !description && weight == 1,
    )
  ) {
    if (viewerCanEdit) {
      return (
        <div>
          <Link variant="secondary" size="sm" to={editLocation}>
            <Pencil01Icon />
            <FormattedMessage defaultMessage="Edit Scoring Criteria" />
          </Link>
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold">
          <FormattedMessage defaultMessage="Scoring Criteria" />
        </h3>

        {viewerCanEdit && (
          <Link variant="secondary" size="sm" to={editLocation}>
            <Pencil01Icon />
            <FormattedMessage defaultMessage="Edit" />
          </Link>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <FormattedMessage defaultMessage="Name" />
            </TableHead>
            <TableHead>
              <FormattedMessage defaultMessage="Description" />
            </TableHead>
            <TableHead>
              <FormattedMessage defaultMessage="Weight" />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scoringCriteria.map(({ id, title, description, weight }) => (
            <TableRow key={id}>
              <TableCell>{title}</TableCell>
              <TableCell>{description}</TableCell>
              <TableCell>
                <FormattedNumber value={weight} style="percent" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
});
